import React, { useState, useEffect } from "react";
import "../style/About.css";
import BuyMeCoffeeButton from '../components/BuyMeCoffeeButton';

const ImageCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resetTimer, setResetTimer] = useState(0);

  const images = [
    "/ulysse/ulysse1.jpg",
    "/ulysse/ulysse2.jpg",
    "/ulysse/ulysse3.jpg",
    "/ulysse/ulysse4.jpg",
    "/ulysse/ulysse5.jpg",
    "/ulysse/ulysse6.jpg",
    "/ulysse/ulysse7.jpg",
    "/ulysse/ulysse8.jpg",
    "/ulysse/ulysse9.jpg",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length, resetTimer]);

  const goToImage = (index) => {
    setCurrentIndex(index);
    setResetTimer(prev => prev + 1); // This will trigger useEffect to restart the timer
  };

  return (
    <div className="carousel-container">
      <img
        src={images[currentIndex]}
        alt={`Ulysse photo ${currentIndex + 1}`}
        className="carousel-image"
      />
      <div className="carousel-dots">
        {images.map((_, index) => (
          <span
            key={index}
            className={`dot ${index === currentIndex ? "active" : ""}`}
            onClick={() => goToImage(index)}
            role="button"
            tabIndex={0}
            aria-label={`Go to image ${index + 1}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                goToImage(index);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};

const About = () => {
  return (
    <div className="about-container">
      <div className="page-header">
        <h1 className="page-title">About The Picky Kitty</h1>
      </div>

      {/* Section 1: The Story */}
      <section id="the-story" className="content-section">
        <h2 className="section-title">The Story</h2>

        <div className="about-flex-container">
          <div className="text-container">
            <p>
              My wife and I have three cats: Ulysse, Hermes, and Themis. It was Ulysse who
              inspired this website.
            </p>

            <p>
              Ulysse has always been the sweetest boy, but also the pickiest eater in the house. We used to laugh
              about it… until December 2024, when our world was turned upside down.
            </p>

            <p>
              At just 4.5 years old, Ulysse spent four days in the emergency room over Christmas, where he was diagnosed with Chronic Kidney Disease.
              Suddenly, our lives revolved around finding the right diet for him, something that would support his kidneys, and that he would actually agree to eat.
            </p>

            <p>Navigating this new world was overwhelming. We spent hours comparing ingredients, checking nutrient levels, and constantly running into conflicting information.</p>

            <p>We tested dozens of foods, trying to balance what was medically appropriate with what Ulysse would actually eat.</p>

            <p>It was exhausting and frustrating. But eventually, we found something that worked for his kidneys, and for him.
            </p>

            <p>That experience is what led me to build The Picky Kitty.</p>
          </div>

          <div className="image-container">
            <img
              src="/ulysse/ulysse.png"
              alt="Ulysse the picky kitty"
              className="rounded-2xl shadow-md"
            />
          </div>
        </div>
      </section>

      <div className="section-divider"></div>

      {/* Section 2: What We Built */}
      <section id="what-we-built" className="content-section">
        <h2 className="section-title">What We Built</h2>

        <div className="section-text-content">
          <p>As a Data Scientist, I built the tool we wish we had: a way to quickly find cat foods that match a cat's needs and something they'll actually eat.</p>

          <p>The database includes 2,000+ products, with nutritional data extracted from manufacturers, and can be filtered by 20+ criteria, from health conditions to protein sources and ingredients to avoid.</p>

          <p>This site is what we needed when Ulysse was diagnosed. It's built to make that process simpler for others facing the same challenge.</p>
        </div>
      </section>

      <div className="section-divider"></div>

      <section className="content-section">
        <h2 className="section-title">Who This Is For</h2>
        <div className="section-text-content">
          <ul className="mission-list">
            <li>Cat owners with picky eaters</li>
            <li>Cat owners managing health conditions (like kidney disease or IBD)</li>
            <li>Cat owners who want clear, transparent information about what's in their cat's food  </li>
            <li>Cat owners who are tired of guessing and want to make informed decisions</li>
          </ul>
        </div>
      </section>

      <div className="section-divider"></div>

      {/* Section 3: What We Stand For */}
      <section id="what-we-stand-for" className="content-section">
        <h2 className="section-title">What We Stand For</h2>

        <div className="section-text-content">
          <p><strong>No ratings. No paid rankings. No marketing bias. Just reliable data.</strong></p>

          <p>The Picky Kitty is built to help you make your own decisions based on data, not opinions.</p>

          <ul className="mission-list">
            <li><strong>No Ratings or Reviews:</strong> We don't score or rank cat food. Every cat is different, and what works for one may not work for another.</li>
            <li><strong>Unbiased Results:</strong> We don't accept paid placements or sponsored rankings. Your results are determined by your filters, not by brands. Affiliate links never influence which products appear.</li>
            <li><strong>Reliable Data:</strong> 2,000+ products with nutritional information sourced directly from manufacturers.</li>
          </ul>
        </div>
      </section>

      <div className="section-divider"></div>

      {/* Section 4: Letter to Ulysse */}
      <section id="letter-to-ulysse" className="content-section">
        <h2 className="section-title">A Letter to Ulysse</h2>

        <div className="ulysse-letter">
          <h3>Dear Ulysse,</h3>
          <p>
            I've written so much because of you. But this time, it's just for you.
          </p>
          <p>
            You're not just a cat. You rule this home, loud, feisty, stubborn, and incredibly picky.
            You know exactly what you want, and you make sure I know it too.
          </p>
          <p>
            And yes, you like to bite me. Not out of anger, just because you can.
            It's your way of showing affection, and somehow one of the things I love most about you.
          </p>
          <p>
            Watching you deal with your illness has been hard. Some days are better than others, but you remain strong, curious, and true to yourself.
            You remind me every day what resilience looks like.
          </p>
          <p>
            This website exists because of you. <em>The Picky Kitty</em> was born out of all the time we spent trying to figure out what you might actually eat, especially after your diagnosis.
          </p>
          <p>
            You've changed my life in ways I never expected. You'll always be my Ulysse boy, my picky kitty.
          </p>
          <p className="signature">Your papa</p>
        </div>
      </section>
      

      <div className="section-divider"></div>

      {/* Section 5: Support */}
      <section id="support" className="content-section">
        <h2 className="section-title">Support The Picky Kitty</h2>

        <div className="section-text-content">
          <p>
          This site is free and always will be. If you'd like to support ongoing development and server costs, you can make a small contribution through <a href="https://www.buymeacoffee.com/thepickykitty" target="_blank" rel="noopener noreferrer">Buy Me a Coffee</a>.
          </p>
          <p>
            Your support helps keep the site running and means a lot to us, and to Ulysse, Hermes, and Themis, who continue to inspire everything we build.
          </p>

          <div className="coffee-button-container">
            <BuyMeCoffeeButton />
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;